import React from 'react';
import { NumberInput, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

import LinkStyleToolbar from '../shared/LinkStyleToolbar';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { contrastRatio, ensureReadable, MIN_CONTRAST_RATIO } from '../../../../shared/colors/colorContrast';

/* La toolbar et la conversion en CSS sont partagées avec le menu d'en-tête
   (`shared/LinkStyleToolbar`) : les deux les écrivaient à l'identique. */
export { linkStyleToCss } from '../shared/LinkStyleToolbar';

/** Mêmes valeurs que le rendu du pied de page, pour dire vrai à l'utilisateur. */
const DEFAULT_LINK_COLOR = '#ffffff';
const DEFAULT_BACKGROUND = '#1a1a1a';

/**
 * Style commun des liens du footer, plus deux choses qui lui appartiennent :
 * l'avertissement de contraste des liens de consentement, et le nombre de
 * colonnes (prop `columns`, écrite via `useSelectedItemProps` — le `onChange`
 * du champ ne couvre que `linkStyle`). Les colonnes vivent ici parce qu'elles
 * changent directement le rendu des liens qu'on est en train de styler.
 */
export default function SiteFooterLinkStyleField({ value, onChange }) {
  const v = value || {};
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const columns = itemProps.columns ?? 2;

  // Les deux liens de consentement héritent de cette couleur, sauf si elle ne
  // se détache pas assez du fond : ils basculent alors en noir ou en blanc.
  // L'utilisateur doit comprendre pourquoi deux liens ne suivent pas son
  // réglage, plutôt que de le prendre pour un bug.
  const linkColor = v.color || DEFAULT_LINK_COLOR;
  const background = itemProps.backgroundColor || DEFAULT_BACKGROUND;
  const ratio = contrastRatio(linkColor, background);
  const forcedColor = ensureReadable(linkColor, background);
  const contrastForced = ratio !== null && forcedColor !== linkColor;

  return (
    <LinkStyleToolbar
      value={value}
      onChange={onChange}
      colorValue={linkColor}
      colorExtra={contrastForced && (
        <Tooltip
          label={`Cette couleur ne se détache pas assez du fond du pied de page (${ratio.toFixed(1)}:1, minimum ${MIN_CONTRAST_RATIO}:1). Vos liens la gardent, mais « Politique cookies » et « Gérer mes cookies » s'afficheront en ${forcedColor === '#ffffff' ? 'blanc' : 'noir'} : la loi exige que l'accès à la politique et le retrait du consentement restent lisibles.`}
          multiline
          w={280}
          withArrow
        >
          <FontAwesomeIcon
            icon={icon({ name: 'circle-info', style: 'light' })}
            style={{ fontSize: 16, display: 'block', color: 'var(--mantine-color-yellow-7)' }}
            aria-label="Contraste insuffisant pour les liens de consentement"
          />
        </Tooltip>
      )}
    >
      {/* Colonnes (variante « Multi-colonnes »). Il y avait ici un aperçu des
          liens : retiré, il n'apprenait rien que le canevas ne montre déjà. */}
      <NumberInput
        label="Colonnes de liens"
        description="Utilisé par le style de footer « Multi-colonnes »."
        size="xs"
        value={columns}
        onChange={(val) => {
          if (val === '' || val == null) return;
          updateItemProps({ columns: Math.max(1, Math.min(4, Math.round(Number(val)))) });
        }}
        min={1}
        max={4}
        step={1}
        clampBehavior="strict"
      />
    </LinkStyleToolbar>
  );
}
